import UIkit from 'uikit';
import { Component } from 'global/interfaces/component.interface';
import Controller from '../../../global/js/controller';
import { MoleculeProductGalleryProps, ProductGalleryItemTypes, ProductGalleryVideoItem } from 'wolf/components/molecules/m-product-gallery/m-product-gallery.data';
import ControllerBase from '../../../../global/js/ControllerBase';
import { OrganismProductGallery360Props } from './o-product-gallery-360.data';
import { MoleculeProductGalleryEvents, MoleculeProductGalleryEventTypes } from 'wolf/components/molecules/m-product-gallery/m-product-gallery.events';
import { OrganismVideoEvents, OrganismVideoEventTypes } from '../o-video/o-video.events';
import { ControllerUnmount } from 'global/interfaces/controller.interface';
const templateMoleculeProductGallery = require('components/molecules/m-product-gallery/m-product-gallery.template.twig') as (
	data: Component<MoleculeProductGalleryProps>,
) => string;


interface ComponentRefs {
	modal: 'modal',
	modalContent: 'modalContent',
	openModalButton: 'openModalButton',
	galleryWrapper: 'galleryWrapper',
}


export default class ControllerProductGallery360 extends ControllerBase<ComponentRefs> {
	private static readonly componentRefs: ComponentRefs = {
		modal: 'modal',
		modalContent: 'modalContent',
		openModalButton: 'openModalButton',
		galleryWrapper: 'galleryWrapper'
	};

	/** The server-rendered component data */
	private readonly data: OrganismProductGallery360Props;
	private readonly openModalButton: HTMLAnchorElement;
	private readonly modal: HTMLDivElement;
	private readonly modalContent: HTMLDivElement;
	// private readonly galleryWrapper: HTMLDivElement;
	// private readonly galleryMolecule: HTMLDivElement | null;

	protected controllersRef: Record<string, typeof Controller & Partial<ControllerUnmount> | undefined>;

	/** The currently selected gallery item to show, init as 0 */
	private currentSelectedIndex = 0;

	constructor(element: HTMLElement) {
		super(element, [], Object.values(ControllerProductGallery360.componentRefs));

		try {
			this.data = this.$element.dataset.data && JSON.parse(this.$element.dataset.data);
		} catch (error) {
			console.log(error);
		}

		this.modal = this.$references.modal as HTMLDivElement;
		this.modalContent = this.$references.modalContent as HTMLDivElement;
		this.openModalButton = this.$references.openModalButton as HTMLAnchorElement;
		// this.galleryWrapper = this.$references.galleryWrapper as HTMLDivElement;
		// this.galleryMolecule = this.galleryWrapper && this.galleryWrapper.querySelector('.m-product-gallery');
		this.init();
	}

	private init(): void {
		if ((window as any).CI360 === undefined) {
			const script = document.createElement('script');
			script.src = 'https://cdn.scaleflex.it/plugins/js-cloudimage-360-view/2/js-cloudimage-360-view.min.js';
			document.head.appendChild(script);
		}
		this.observeFullScreenClicks();
		this.observePreviewIndexChange();
		this.observeModalOpeningAndClosing();
	}

	private observeFullScreenClicks(): void {
		this.openModalButton && this.openModalButton.addEventListener('click', e => this.goFullScreen(e));
	}

	/**
	 * Inject the product gallery molecule into the empty modal
	 * @param  {Event|MouseEvent} event
	 * @returns void
	 */

	private goFullScreen(event: Event | MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		if (!this.modalContent) return;

		// render the modal contents
		const modalRefStr: ComponentRefs['modalContent'] = 'modalContent';
		const moleculeData: MoleculeProductGalleryProps = { ...this.data.gallery };
		const newData: MoleculeProductGalleryProps = {
			...moleculeData,
			currentlySelected: this.currentSelectedIndex,
			fullscreen: true,
		};
		newData.mediaItems.map(i => {
			if (i.type === ProductGalleryItemTypes.Video) return new ProductGalleryVideoItem(i);
			return i;
		});

		const html = templateMoleculeProductGallery({ data: { ...{ ...newData, vertical: true } } });
		this.domUtils.renderDomElement(modalRefStr, html);
		this.initializeNestedComponents();
		// re-init any newly rendered 360 players
		this.reinit360Player();
		this.modal && UIkit.modal(this.modal).show();
	}

	private observePreviewIndexChange(): void {
		// listen for inner gallery molecule custom events, when user chooses a new thumb
		window.addEventListener(MoleculeProductGalleryEvents.thumbnailChanged, (e: CustomEvent<MoleculeProductGalleryEventTypes['thumbnailChanged']>) => {
			// index of the new thumbnail selected
			this.currentSelectedIndex = e.detail;
		});
	}

	private observeModalOpeningAndClosing(): void {

		if (!this.modal) return;

		// handle opening of modal
		this.modal.addEventListener('beforeshow', e => {
			// tell all videos to stop regardless!
			this.dispatchCustomEvent<OrganismVideoEventTypes['pauseAll']>(OrganismVideoEvents.pauseAll, true, true, true);
		});

		// handle closing of modal
		this.modal.addEventListener('beforehide', e => {
			// tell all videos to stop regardless!
			this.dispatchCustomEvent<OrganismVideoEventTypes['stopAll']>(OrganismVideoEvents.stopAll, true, true, true);
			// cleanup DOM
			this.removeModalContent();
		});

	}

	/**
	 * Remove HTML from the DOM inside the modal
	 * @returns void
	 */
	private removeModalContent(): void {
		const modalRefStr: ComponentRefs['modalContent'] = 'modalContent';
		if (this.modalContent) {
			this.domUtils.renderDomElement(modalRefStr, '');
			this.initializeNestedComponents();
		}
	}

	/**
	 * Initializes any uninitialized cloudimage 360 instances in the DOM
	 * @link https://github.com/scaleflex/js-cloudimage-360-view#init
	 * @returns void
	 */
	private reinit360Player(): void {
		(window as any).CI360 && (window as any).CI360.init();
	}

	/**
	 * Instantiate a new controller for the modal's nested content
	 * @returns void
	 */
	private initializeNestedComponents(): void {
		// see this: src/global/react/components/HydratedTwigHtmlString/HydratedTwigHtmlString.tsx
		// Cleaning up the class references in memory
		this.controllersRef && Object.keys(this.controllersRef).forEach((key) => {
			if (this.controllersRef && this.controllersRef[key] !== undefined) {
				// for controllers requiring additional cleanup tasks
				if (this.controllersRef[key]?.onControllerUnmount) {
					(this.controllersRef[key] as typeof Controller & ControllerUnmount).onControllerUnmount();
				}
				this.controllersRef[key] = undefined;
			}
		});


		const components = this.modal.querySelectorAll('[data-controller]');

		this.controllersRef = {};
		components.forEach((element: HTMLElement, i: number) => {
			const controllerName = element.dataset.controller as string;
			this.controllersRef = {
				...this.controllersRef,
				[`tt${i}-${controllerName}`]: new Controller[controllerName](element),
			};
		});
	}
}
